// Maps game events to VFX triggers. Each event type that should produce
// a visual effect gets a function that extracts position + picks the
// VFX id (and optional sound) from the event payload.
//
// Events without an entry return null and produce no VFX.

import type { GameEvent } from '@aod/protocol';

/** Structural event shape — accepts GameEvent or the loose event type used by useSimWorld. */
type AnyEvent = GameEvent | { type: string; payload?: Record<string, unknown> };

export interface VfxTrigger {
  readonly vfxId: string;
  readonly position: readonly [number, number, number];
  readonly soundId?: string;
  readonly beamTargetOverride?: readonly [number, number, number];
  /** If present and vfxId === 'projectile:arrow', spawns a Projectile
   * from `position` to this target instead of the standard VFX. */
  readonly projectileTarget?: readonly [number, number, number];
}

type Payload = Record<string, unknown> | undefined;

function vec3FromPayload(
  payload: Payload,
  ...keys: readonly string[]
): readonly [number, number, number] | null {
  for (const key of keys) {
    const v = payload?.[key];
    if (Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number')) {
      const a = v[0] as number;
      const b = v[1] as number;
      const c = v[2] as number;
      return [a, b, c];
    }
  }
  return null;
}

const DEFAULT_POS: readonly [number, number, number] = [0, 0, 0];

function pos(
  e: AnyEvent,
  fallback: readonly [number, number, number] = DEFAULT_POS,
): readonly [number, number, number] {
  return vec3FromPayload(e.payload as Payload, 'position', 'worldPos', 'pos') ?? fallback;
}

function posKey(
  e: AnyEvent,
  key: string,
  fallback: readonly [number, number, number] = DEFAULT_POS,
): readonly [number, number, number] {
  return vec3FromPayload(e.payload as Payload, key) ?? fallback;
}

type VfxMapper = (e: AnyEvent) => VfxTrigger | null;

const EVENT_VFX_MAP: Record<string, VfxMapper> = {
  'unit:combat': (e) => ({
    vfxId: 'hit:melee_spark',
    position: pos(e),
  }),
  'unit:ranged_attack': (e) => ({
    vfxId: 'projectile:arrow',
    position: posKey(e, 'origin'),
    projectileTarget: posKey(e, 'target'),
    soundId: 'shoot_arrow',
  }),
  'unit:died': (e) => ({
    vfxId: 'hit:blood_puff',
    position: pos(e),
  }),
  'city:damaged': (e) => ({
    vfxId: 'hit:melee_spark',
    position: pos(e),
  }),
  'mage:cast:healing_light': (e) => ({
    vfxId: 'spell:healing_light',
    position: pos(e),
    soundId: 'spell_heal',
  }),
  'mage:cast:teleport': (e) => ({
    vfxId: 'spell:teleport',
    position: pos(e),
    soundId: 'spell_teleport',
  }),
  'mage:cast:divine_shield': (e) => ({
    vfxId: 'spell:divine_shield',
    position: pos(e),
    soundId: 'spell_shield',
  }),
  'mage:cast:summon': (e) => ({
    vfxId: 'spell:summon_skeleton',
    position: pos(e),
    soundId: 'spell_summon',
  }),
  'lich:cast:drain': (e) => ({
    vfxId: 'spell:lich_drain',
    position: pos(e),
    soundId: 'spell_drain',
  }),
  'faith:sanctified': (e) => ({
    vfxId: 'faith:sanctified',
    position: pos(e, [0, 1, 0]),
    soundId: 'sanctified_pulse',
  }),
  'faith:crisis_triggered': (e) => ({
    vfxId: 'faith:crisis_triggered',
    position: pos(e, [0, 5, 0]),
    soundId: 'faith_crisis',
  }),
  'building:completed': (e) => ({
    vfxId: 'construction:building_complete',
    position: pos(e),
  }),
};

export function eventToVfx(event: AnyEvent): VfxTrigger | null {
  const fn = EVENT_VFX_MAP[event.type];
  return fn ? fn(event) : null;
}
